import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Alert,
  DialogActions,
  DialogContent,
  Divider,
  Grid,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";
import Input from "../../../../components/ui/Input";
import Select from "../../../../components/ui/Select";
import Checkbox from "../../../../components/ui/Checkbox";
import LoadingButton from "../../../../components/ui/LoadingButton";
import { useIntl } from "../../../../contexts/IntlContext";
import api from "../../../../services/api";
import { YoutubeOwnerConnectionStatus } from "../../../../services/youtubeOwnerConnection";
import {
  useAbandonLivePeriodPart,
  useCreateLivePeriod,
  useLivePeriodPreview,
  useLivePeriodStatus,
  useRetryLivePeriodPart,
  LivePeriodPartResult,
  LivePeriodResult,
} from "../../../../hooks/useLivePeriod";
import {
  CameraStatusRow,
  EstablishmentWithChannels,
  LivePlaylistOption,
  YoutubeAccountOption,
} from "../../types";
import DeviceSelector, { CameraOption } from "./DeviceSelector";
import { formatDateTimeParts, timeBoundFor, useScheduleLimits } from "../../../../hooks/useScheduleLimits";
import PeriodRangeFields from "./PeriodRangeFields";
import DailyTimeRangeFields from "./DailyTimeRangeFields";
import LivePeriodPreviewSummary from "./LivePeriodPreviewSummary";
import LivePeriodScheduleList from "./LivePeriodScheduleList";
import { fontWeights } from "../../../../themes/fontWeights";
import { OWNER_YOUTUBE_CONNECTION_REQUIRED } from "../../../../constants/ownerYoutubePolicy";

type Privacy = "PUBLIC" | "UNLISTED" | "PRIVATE";

const DEFAULT_BACKGROUND_MUSIC_PATH = "music/playlists/random/";

type Props = {
  onClose: () => void;
  establishments: EstablishmentWithChannels[];
  setEstablishments: Dispatch<SetStateAction<EstablishmentWithChannels[]>>;
  establishmentsReady: boolean;
  cameraOptions: CameraStatusRow[];
  selectedEstablishmentId: string;
  onEstablishmentChange: (establishmentId: string) => void;
  templatesLoading: boolean;
  livePlaylists: LivePlaylistOption[];
  setLivePlaylists: Dispatch<SetStateAction<LivePlaylistOption[]>>;
  playlistListContextLiveId: number | null;
  isOwnerOnly: boolean;
  isLocalhost: boolean;
  youtubeConnectionStatus: YoutubeOwnerConnectionStatus | null;
  youtubeConnectionBlocked: boolean;
  onCreated?: () => void;
};

/** OWNER pode planejar até este número de dias por período — mesmo teto aplicado no servidor (`LivePeriodService#OWNER_MAX_DAYS`), aqui só pra dar feedback antes de enviar a request. */
const OWNER_MAX_DAYS = 3;

/** Diferença inclusiva em dias entre duas datas "YYYY-MM-DD" (01/08 a 03/08 = 3 dias). */
const daysBetween = (start: string, end: string) => {
  if (!start || !end) return 0;
  const [sy, sm, sd] = start.split("-").map(Number);
  const [ey, em, ed] = end.split("-").map(Number);
  if (!sy || !sm || !sd || !ey || !em || !ed) return 0;
  const startUtc = Date.UTC(sy, sm - 1, sd);
  const endUtc = Date.UTC(ey, em - 1, ed);
  return Math.round((endUtc - startUtc) / 86_400_000) + 1;
};

/**
 * Fluxo de agendamento (só OWNER — ADMIN/SUPPORT sempre tem `canOperate=true` e usa
 * `SingleLiveCreationFlow`, nunca chega aqui): sempre pede câmera + período + horário diário
 * único, mesmo quando o período é de 1 dia só — não existe mais um formulário separado para "um
 * dia com hora exata", porque a diária cobre igualmente 1 ou N dias.
 * O backend gera automaticamente um LiveSchedule por dia (dividido em Parte 1/Parte 2 quando a
 * duração diária passa de 9h) e devolve o valor total (`billedAmount = diária × dias`) tanto no
 * preview quanto na criação. OWNER pode planejar até `OWNER_MAX_DAYS` dias por período (antes era
 * travado em 1 dia); o billing só é cobrado quando todas as partes do período forem resolvidas
 * (criadas ou desistidas) — enquanto isso, a tela faz polling do status e avisa quando o pagamento
 * for liberado.
 *
 * Renderizado como conteúdo dentro do `<Dialog>` compartilhado de `LiveCreationDialog` — o
 * título/botão de fechar são do shell, não deste componente.
 */
export default function LivePeriodCreationFlow({
  onClose,
  establishments,
  setEstablishments,
  establishmentsReady,
  cameraOptions,
  selectedEstablishmentId,
  onEstablishmentChange,
  templatesLoading,
  livePlaylists,
  setLivePlaylists,
  playlistListContextLiveId,
  isOwnerOnly,
  isLocalhost,
  youtubeConnectionStatus,
  youtubeConnectionBlocked,
  onCreated,
}: Props) {
  const intl = useIntl();
  const navigate = useNavigate();
  const [selectedDeviceId, setSelectedDeviceId] = useState<number | null>(null);
  // Primeiro início permitido no fuso do clube (null para ADMIN/SUPPORT): apaga dias/horários anteriores.
  const { limits: scheduleLimits, earliest: earliestStart } = useScheduleLimits(selectedDeviceId);
  const [liveName, setLiveName] = useState("");
  const [playlistId, setPlaylistId] = useState<number | "">("");
  const [privacy, setPrivacy] = useState<Privacy>("PUBLIC");
  const [enableAudio, setEnableAudio] = useState(false);
  const [enableBackgroundMusic, setEnableBackgroundMusic] = useState(true);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [dailyStartTime, setDailyStartTime] = useState("");
  const [dailyEndTime, setDailyEndTime] = useState("");
  const [livePlaylistsReady, setLivePlaylistsReady] = useState(false);
  // TEMPORÁRIO: OWNER pode escolher outra conta YouTube (vazio = a conta que ele conectou).
  const [youtubeAccounts, setYoutubeAccounts] = useState<YoutubeAccountOption[]>([]);
  const [selectedYoutubeAccountId, setSelectedYoutubeAccountId] = useState<number | "">("");
  // Cópia local do resultado da criação: permite refletir na hora o efeito de um retry bem
  // sucedido (a peça FAILED vira CREATED) sem precisar reconsultar os schedules da Live.
  const [localResult, setLocalResult] = useState<LivePeriodResult | null>(null);

  const { preview, loading: previewLoading, error: previewError, fetchPreview, clear: clearPreview } =
    useLivePeriodPreview();
  const { result, loading: creating, error: createError, create } = useCreateLivePeriod();
  const { retry, retryingSlotId, error: retryError } = useRetryLivePeriodPart();
  const { abandon, abandoningSlotId, error: abandonError } = useAbandonLivePeriodPart();

  // Com a conexão própria obrigatória o OWNER não escolhe conta: sem seletor e sem a busca de contas.
  const ownerCanPickAccount = isOwnerOnly && !OWNER_YOUTUBE_CONNECTION_REQUIRED;

  // Sem essa busca, `livePlaylists` fica vazio: quem preenche este formulário nunca passa pelo
  // SingleLiveCreationFlow (só usado no modo operacional), então nada mais no app popula essa lista.
  useEffect(() => {
    if (!ownerCanPickAccount) return;
    let canceled = false;
    const loadYoutubeAccounts = async () => {
      try {
        const response = await api.get<{ data: Array<{ id: number; name: string }> }>(
          "/app/lives/youtube-accounts",
        );
        const rows = Array.isArray(response.data?.data) ? response.data.data : [];
        const normalized: YoutubeAccountOption[] = rows
          .map((r: any) => ({ id: Number(r.id), name: String(r.name ?? r.id) }))
          .filter((r) => Number.isFinite(r.id) && !!r.name);
        normalized.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
        if (!canceled) setYoutubeAccounts(normalized);
      } catch {
        if (!canceled) setYoutubeAccounts([]);
      }
    };
    void loadYoutubeAccounts();
    return () => {
      canceled = true;
    };
  }, [ownerCanPickAccount]);

  useEffect(() => {
    let canceled = false;
    const loadPlaylists = async () => {
      if (!establishmentsReady) return;
      try {
        const params: Record<string, number> = {};
        if (playlistListContextLiveId != null) params.liveId = playlistListContextLiveId;
        if (ownerCanPickAccount && selectedYoutubeAccountId !== "") {
          params.youtubeAccountId = selectedYoutubeAccountId;
        }
        const response = await api.get<{ data: Array<{ id: number; name: string }> }>(
          "/app/lives/playlists",
          { ...(Object.keys(params).length ? { params } : {}) },
        );
        const rows = Array.isArray(response.data?.data) ? response.data.data : [];
        const normalized: LivePlaylistOption[] = rows
          .map((r: any) => ({ id: Number(r.id), name: String(r.name ?? r.id) }))
          .filter((r) => Number.isFinite(r.id) && !!r.name);
        normalized.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
        if (!canceled) setLivePlaylists(normalized);
      } catch {
        if (!canceled) setLivePlaylists([]);
      } finally {
        if (!canceled) setLivePlaylistsReady(true);
      }
    };
    void loadPlaylists();
    return () => {
      canceled = true;
    };
  }, [establishmentsReady, playlistListContextLiveId, ownerCanPickAccount, selectedYoutubeAccountId, setLivePlaylists]);

  // Se o OWNER não tem playlist nenhuma na conta YouTube conectada, cria uma automaticamente —
  // sem isso, o formulário fica bloqueado (nenhuma opção pra escolher) até um admin intervir.
  useEffect(() => {
    if (!isOwnerOnly) return;
    if (selectedYoutubeAccountId !== "") return;
    if (!youtubeConnectionStatus?.connected) return;
    if (!livePlaylistsReady) return;
    if (livePlaylists.length > 0) return;
    const establishment = establishments.find((e) => e.id === selectedEstablishmentId);
    if (!establishment) return;
    if (typeof establishment.livePlaylistId === "number") return;

    let canceled = false;
    const createPlaylist = async () => {
      try {
        const response = await api.post<{ data: { id: number } }>("/app/lives/playlists", {
          name: "Transmissões ao Vivo",
          establishmentId: selectedEstablishmentId,
        });
        const newPlaylistId = response.data?.data?.id;
        if (canceled || !newPlaylistId) return;
        setPlaylistId(newPlaylistId);
        setEstablishments((prev) =>
          prev.map((e) => (e.id === selectedEstablishmentId ? { ...e, livePlaylistId: newPlaylistId } : e)),
        );
        const playlistRes = await api.get<{ data: Array<{ id: number; name: string }> }>(
          "/app/lives/playlists",
        );
        const rows = Array.isArray(playlistRes.data?.data) ? playlistRes.data.data : [];
        const normalized = rows
          .map((r: any) => ({ id: Number(r.id), name: String(r.name ?? r.id) }))
          .filter((r) => Number.isFinite(r.id) && !!r.name);
        normalized.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
        if (!canceled) setLivePlaylists(normalized);
      } catch {
        // se falhar, o usuário ainda pode tentar de novo reabrindo o dialog
      }
    };
    void createPlaylist();
    return () => {
      canceled = true;
    };
  }, [
    isOwnerOnly,
    selectedYoutubeAccountId,
    youtubeConnectionStatus,
    livePlaylistsReady,
    livePlaylists,
    establishments,
    selectedEstablishmentId,
    setEstablishments,
    setLivePlaylists,
  ]);

  const buildPayload = () => ({
    deviceId: selectedDeviceId as number,
    playlistId: playlistId as number,
    name: liveName || undefined,
    privacy,
    enableAudio,
    musicPath: enableBackgroundMusic ? DEFAULT_BACKGROUND_MUSIC_PATH : null,
    startDate,
    endDate,
    dailyStartTime,
    dailyEndTime,
  });

  // Comparação de string funciona porque os dois campos são sempre "HH:mm" (24h, zero-padded) —
  // ordem lexicográfica coincide com ordem cronológica. Pega na hora o erro mais comum do seletor de
  // horário (esquecer de trocar AM/PM), em vez de só descobrir depois de tentar criar.
  const dailyTimeInvalid =
    !!dailyStartTime && !!dailyEndTime && dailyEndTime <= dailyStartTime;

  const ownerRangeInvalid =
    isOwnerOnly && !!startDate && !!endDate && daysBetween(startDate, endDate) > OWNER_MAX_DAYS;

  // Valores digitados ou preenchidos antes dos limites chegarem não são apagados do estado,
  // então o primeiro dia também é validado aqui (não só desabilitado nos seletores).
  const startTooSoon =
    !!earliestStart &&
    !!startDate &&
    !!dailyStartTime &&
    `${startDate}T${dailyStartTime}` < `${earliestStart.date}T${earliestStart.time}`;

  const isFormReady =
    !!selectedDeviceId &&
    !!playlistId &&
    !!startDate &&
    !!endDate &&
    !!dailyStartTime &&
    !!dailyEndTime &&
    !dailyTimeInvalid &&
    !startTooSoon &&
    !ownerRangeInvalid;

  // Qualquer alteração nos campos depois de um preview invalida o resumo mostrado — evita
  // confirmar uma Live com datas/horário diferentes do que o usuário viu no resumo.
  const withFieldChange =
    <T,>(setter: (value: T) => void) =>
    (value: T) => {
      if (preview) clearPreview();
      setter(value);
    };

  const handleStartDateChange = (value: string) => {
    if (preview) clearPreview();
    setStartDate(value);
  };

  const handlePreview = async () => {
    if (!isFormReady) return;
    await fetchPreview(buildPayload());
  };

  const handleConfirm = async () => {
    if (!isFormReady) return;
    const created = await create(buildPayload());
    onCreated?.();
    // Só o caso trivial (1 dia, sem split, e a única parte deu certo) pula direto pros detalhes da
    // live sem passar pela tela de resultado com retry/desistir. A tela de detalhes não sabe nada
    // sobre LivePeriodScheduleSlot — se pular a tela de resultado com a parte em FAILED/SKIPPED, o
    // OWNER nunca veria o erro nem teria como tentar de novo, e o período ficaria preso em
    // AWAITING_SCHEDULES pra sempre. Qualquer período com mais de um dia, dia dividido em Parte
    // 1/Parte 2 (agora possível pro OWNER também, até OWNER_MAX_DAYS dias), ou falha na única parte
    // precisa dessa tela.
    const isSingleDaySingleCreatedPart =
      created?.days?.length === 1 &&
      created.days[0]?.parts?.length === 1 &&
      created.days[0]?.parts[0]?.status === "CREATED";
    if (isOwnerOnly && isSingleDaySingleCreatedPart && created?.liveId) {
      navigate(`/transmissoes-ao-vivo/live/${created.liveId}?tab=detalhes`);
      return;
    }
    setLocalResult(created);
  };

  const applyPartUpdate = (slotId: number, updated: LivePeriodPartResult | null) => {
    setLocalResult((current) => {
      if (!current || !updated) return current;
      return {
        ...current,
        days: current.days.map((day) => ({
          ...day,
          parts: day.parts.map((p) => (p.slotId === slotId ? { ...p, ...updated } : p)),
        })),
      };
    });
  };

  const handleRetry = async (part: LivePeriodPartResult) => {
    if (!localResult?.liveId || part.slotId == null) return;
    try {
      const updated = await retry(localResult.liveId, part.slotId);
      applyPartUpdate(part.slotId, updated);
    } catch {
      // erro já fica em retryError
    }
  };

  const handleAbandon = async (part: LivePeriodPartResult) => {
    if (!localResult?.liveId || part.slotId == null) return;
    try {
      const updated = await abandon(localResult.liveId, part.slotId);
      applyPartUpdate(part.slotId, updated);
    } catch {
      // erro já fica em abandonError
    }
  };

  const handleClose = () => {
    clearPreview();
    setLocalResult(null);
    onClose();
  };

  const shownResult = localResult ?? result;

  // Polling de status: enquanto o período tiver partes pendentes, o link de pagamento ainda não
  // existe (billing fica em AWAITING_SCHEDULES). Mesmo padrão de intervalo usado em Payment.tsx —
  // não existe push/WebSocket no app hoje.
  const { status: periodStatus } = useLivePeriodStatus(shownResult?.liveId ?? null);
  const billingConfirmedNotified = useRef(false);
  const periodPending = periodStatus?.periodPending ?? shownResult?.periodPending;

  useEffect(() => {
    if (periodStatus?.periodPending === false && !billingConfirmedNotified.current) {
      billingConfirmedNotified.current = true;
      toast.success(intl.formatMessage({ id: "liveStreams.period.billingConfirmed" }));
    }
  }, [periodStatus, intl]);

  return (
    <>
      <DialogContent>
        {shownResult ? (
          <>
            {periodPending && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {intl.formatMessage({ id: "liveStreams.period.billingAwaiting" })}
              </Alert>
            )}
            <LivePeriodScheduleList
              result={shownResult}
              onRetryPart={handleRetry}
              onAbandonPart={handleAbandon}
              retryingSlotId={retryingSlotId}
              abandoningSlotId={abandoningSlotId}
            />
            {retryError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {retryError}
              </Alert>
            )}
            {abandonError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {abandonError}
              </Alert>
            )}
          </>
        ) : (
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            {youtubeConnectionBlocked ? (
              <Grid item xs={12}>
                <Alert severity="warning">
                  {intl.formatMessage({ id: "youtubeConnection.blockingWarning" })}
                </Alert>
              </Grid>
            ) : null}
            <DeviceSelector
              establishments={establishments}
              selectedEstablishmentId={selectedEstablishmentId}
              onEstablishmentChange={withFieldChange(onEstablishmentChange)}
              cameraOptions={cameraOptions}
              selectedDeviceId={selectedDeviceId}
              onDeviceChange={withFieldChange((device: CameraOption | null) =>
                setSelectedDeviceId(device?.deviceId ?? null),
              )}
              loading={templatesLoading}
            />
            <Grid item xs={12}>
              <Input
                size="small"
                label={intl.formatMessage({ id: "liveStreams.name" })}
                value={liveName}
                onChange={(e) => withFieldChange(setLiveName)(e.target.value)}
                fullWidth
              />
            </Grid>
            {ownerCanPickAccount ? (
              <Grid item xs={12} md={6}>
                <Select
                  label={intl.formatMessage({ id: "liveStreams.youtubeAccount" })}
                  value={selectedYoutubeAccountId}
                  onChange={(e) => {
                    withFieldChange(setSelectedYoutubeAccountId)(Number(e.target.value) || "");
                    setLivePlaylists([]);
                    setPlaylistId("");
                  }}
                >
                  <MenuItem value="">
                    {intl.formatMessage({ id: "liveStreams.youtubeAccountOwn" })}
                  </MenuItem>
                  {youtubeAccounts.map((a) => (
                    <MenuItem key={a.id} value={a.id}>
                      {a.name}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>
            ) : null}
            <Grid item xs={12} md={6}>
              <Select
                label={intl.formatMessage({ id: "liveStreams.playlist" })}
                value={playlistId}
                onChange={(e) => withFieldChange(setPlaylistId)(Number(e.target.value) || "")}
                disabled={!youtubeConnectionStatus?.connected && selectedYoutubeAccountId === ""}
                helperText={
                  !youtubeConnectionStatus?.connected && selectedYoutubeAccountId === ""
                    ? intl.formatMessage({ id: "liveStreams.disabledYoutubeNotConnected" })
                    : undefined
                }
              >
                {livePlaylists.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.name}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid item xs={12} md={6}>
              <Select
                label={intl.formatMessage({ id: "liveDetails.privacy" })}
                value={privacy}
                onChange={(e) => withFieldChange(setPrivacy)(String(e.target.value) as Privacy)}
              >
                <MenuItem value="PUBLIC">
                  {intl.formatMessage({ id: "liveDetails.privacyPublic" })}
                </MenuItem>
                <MenuItem value="UNLISTED">
                  {intl.formatMessage({ id: "liveDetails.privacyUnlisted" })}
                </MenuItem>
                <MenuItem value="PRIVATE">
                  {intl.formatMessage({ id: "liveDetails.privacyPrivate" })}
                </MenuItem>
              </Select>
            </Grid>
            <Grid item xs={12}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
                <Checkbox
                  label={intl.formatMessage({ id: "liveDetails.enableAudio" })}
                  checkboxProps={{
                    checked: enableAudio,
                    onChange: (e) => withFieldChange(setEnableAudio)(e.target.checked),
                  }}
                />
                <Checkbox
                  label={intl.formatMessage({ id: "liveDetails.backgroundMusic" })}
                  checkboxProps={{
                    checked: enableBackgroundMusic,
                    onChange: (e) => withFieldChange(setEnableBackgroundMusic)(e.target.checked),
                  }}
                />
              </Stack>
            </Grid>
            <PeriodRangeFields
              startDate={startDate}
              endDate={endDate}
              onStartDateChange={handleStartDateChange}
              minStartDate={earliestStart?.date}
              onEndDateChange={withFieldChange(setEndDate)}
              error={
                ownerRangeInvalid
                  ? intl.formatMessage(
                      { id: "liveStreams.period.ownerMaxDaysExceeded" },
                      { days: OWNER_MAX_DAYS },
                    )
                  : undefined
              }
            />
            <DailyTimeRangeFields
              dailyStartTime={dailyStartTime}
              dailyEndTime={dailyEndTime}
              onDailyStartTimeChange={withFieldChange(setDailyStartTime)}
              minStartTime={timeBoundFor(startDate, earliestStart)}
              startTimeError={startTooSoon}
              startTimeHelperText={
                earliestStart
                  ? startTooSoon
                    ? intl.formatMessage(
                        { id: "liveStreams.startTooSoon" },
                        { minutes: scheduleLimits?.minLeadMinutes, time: formatDateTimeParts(earliestStart) },
                      )
                    : intl.formatMessage(
                        { id: "liveStreams.period.startWindowHint" },
                        { from: formatDateTimeParts(earliestStart) },
                      )
                  : undefined
              }
              onDailyEndTimeChange={withFieldChange(setDailyEndTime)}
              error={
                dailyTimeInvalid
                  ? intl.formatMessage({ id: "liveStreams.period.dailyTimeInvalid" })
                  : undefined
              }
            />
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <LivePeriodPreviewSummary preview={preview} loading={previewLoading} error={previewError} />
              {isOwnerOnly && preview && (
                <Typography variant="body2" sx={{ mt: 1, color: "warning.main", fontWeight: fontWeights.bold }}>
                  {intl.formatMessage({ id: "liveStreams.ownerConfirmNotice" })}
                </Typography>
              )}
            </Grid>
            {createError && createError !== previewError && (
              <Grid item xs={12}>
                <Alert severity="error">{createError}</Alert>
              </Grid>
            )}
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <LoadingButton onClick={handleClose} variant="text">
          {intl.formatMessage({ id: shownResult ? "global.close" : "global.cancel" })}
        </LoadingButton>
        {shownResult?.liveId && (
          <LoadingButton
            onClick={() => {
              handleClose();
              navigate(`/transmissoes-ao-vivo/live/${shownResult.liveId}?tab=detalhes`);
            }}
            variant="outlined"
          >
            {intl.formatMessage({ id: "liveStreams.period.viewDetails" })}
          </LoadingButton>
        )}
        {!shownResult && (
          <>
            <LoadingButton
              onClick={handlePreview}
              loading={previewLoading}
              disabled={!isFormReady}
              variant="outlined"
            >
              {intl.formatMessage({ id: "liveStreams.period.preview" })}
            </LoadingButton>
            <LoadingButton
              onClick={handleConfirm}
              loading={creating}
              disabled={!isFormReady || !preview}
            >
              {intl.formatMessage({ id: "liveStreams.period.confirm" })}
            </LoadingButton>
          </>
        )}
      </DialogActions>
    </>
  );
}
